/**
 * Карточка рабочего места (p2-ot-section §9.5, design D5) — READ-ONLY: подразделение/должность/
 * признак «без сотрудника»/сотрудник(и)/сменщики/сведения СОУТ/сведения профрисков/связанные
 * документы/статус/хронология СОУТ и профрисков (F2-13). Редактирование/удаление места остаётся
 * в разделе «Сотрудники» → «Структура» (design D5) — карточка не даёт правки места/сотрудников.
 * Исключение (p3-structure-sout-training §3, design D5): кнопка «Добавить историческую СОУТ»,
 * открывает форму единой панели `HistoricalSoutPanel` с автоподстановкой места (`workplace.id`);
 * внесённые исторические записи показаны сразу, с «Править»/«Удалить» (`inlineRecords`);
 * профриски НЕ расширяем (ТЗ §1.2 — только карточка сотрудника). Доступна и для вакантных, и
 * для занятых мест. Панель живёт ВНУТРИ карточки «Сведения по СОУТ», под сведениями о последнем
 * результате или пустым состоянием (centrika-docs!28, по образцу карточки сотрудника) —
 * «Хронология СОУТ» остаётся отдельной карточкой и кнопки не содержит.
 */
import { type ReactNode, Fragment } from 'react'
import { ShieldCheck, AlertTriangle, FileText, History } from 'lucide-react'
import type { EmployeeSummary, WorkplaceCard, WorkplaceHistorySource } from '@/lib/api'
import { OT_STATUS_VIEW } from '@/lib/ot'
import { RISK_LEVEL_LABELS } from '@/lib/profrisk'
import { formatWorkHours } from '@/lib/work-hours'
import { AppLink } from '@/app/navigation'
import { HistoricalSoutPanel } from '@/sout/historical-sout-form'
import { Card } from '@/design-system/components/layout/card'
import { Grid } from '@/design-system/components/layout/grid'
import { Badge } from '@/design-system/components/primitives/badge'
import { Button } from '@/design-system/components/primitives/button'
import { EmptyState } from '@/design-system/components/feedback/empty-state'

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center gap-3 border-b border-line py-2.5 last:border-b-0">
      <div className="flex-1 text-txt-secondary">{label}</div>
      <span className="text-right font-medium text-txt">{value || '—'}</span>
    </div>
  )
}

function formatDate(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Сотрудники/сменщики как отдельные кликабельные ссылки на карточку сотрудника (F2-13),
 * с сохранением визуального разделителя «, » между именами (было — неинтерактивный .join).
 * `from` (D1, p3-structure-sout-training §2) — адрес этой же карточки места, передаётся как
 * `state.from`, чтобы «Назад» в открытой карточке сотрудника вернул именно сюда (цепочка
 * «место → сотрудник»). */
function employeeLinks(employees: EmployeeSummary[], from?: string): ReactNode {
  return employees.map((e, i) => (
    <Fragment key={e.id}>
      {i > 0 ? ', ' : ''}
      <AppLink to={`/staff/${e.id}`} state={from ? { from } : undefined}>
        {e.fullName}
      </AppLink>
    </Fragment>
  ))
}

/** Источник записи хронологии (F2-13): «Заказ от {дата}» (кликабельно на карточку заказа, если
 * есть orderId) либо «Историческая запись» (source_order_id IS NULL, p2-manual-historical). */
function HistorySource({ source }: { source: WorkplaceHistorySource }) {
  if (source.kind === 'order') {
    const label = `Заказ от ${source.orderDate ? formatDate(source.orderDate) : '—'}`
    return source.orderId ? <AppLink to={`/orders/${source.orderId}`}>{label}</AppLink> : <span>{label}</span>
  }
  return <span>Историческая запись</span>
}

/** Одна запись хронологии: у СОУТ и профрисков общий каркас (id + источник), различаются только
 * основным текстом (`primary`) и датой (`dateLabel`) — извлечено, чтобы блоки «Хронология СОУТ»/
 * «Хронология профрисков» не были копипастом (F2-13). */
interface HistoryEntry {
  id: string
  source: WorkplaceHistorySource
}

/** Карточка хронологии (F2-13): пустое состояние ИЛИ список записей, свежие сверху (сортировка —
 * на сервере). Разметка/тексты идентичны прежним инлайн-блокам — чистый рефактор. */
function HistoryCard<T extends HistoryEntry>({
  header,
  items,
  primary,
  dateLabel,
}: {
  header: string
  items: T[]
  primary: (item: T) => ReactNode
  dateLabel: (item: T) => string
}) {
  return (
    <Card header={header}>
      {items.length === 0 ? (
        <EmptyState icon={History} title="Хронологии пока нет" />
      ) : (
        <div>
          {items.map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-3 border-b border-line py-2.5 last:border-b-0">
              <div>
                <div className="font-medium text-txt">{primary(item)}</div>
                <div className="text-13 text-txt-secondary">
                  <HistorySource source={item.source} />
                </div>
              </div>
              <span className="text-13 text-txt-secondary">{dateLabel(item)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

export function WorkplaceCardView({
  workplace,
  onSoutHistoricalSaved,
  backFrom,
  onEdit,
  onDelete,
  deleting,
}: {
  workplace: WorkplaceCard
  /**
   * Историческая СОУТ сохранена через `HistoricalSoutPanel` блока «Сведения по СОУТ» (§3,
   * p3-structure-sout-training, code-review волны 2; размещение — centrika-docs!28) —
   * опционально: без обработчика панель
   * по-прежнему работает (свой список перезагружается сама), но сводка «Сведения по СОУТ» и
   * «Хронология СОУТ» не обновятся до следующего открытия карточки. `pages/workplace.tsx`
   * передаёт `reloadToken`-бамп по образцу `employee-card-loader.tsx`.
   */
  onSoutHistoricalSaved?: () => void
  /**
   * Адрес текущей карточки места (D1, p3-structure-sout-training §2) — передаётся в ссылки на
   * сотрудников/сменщиков как `state.from`, чтобы их «Назад» вернул на эту карточку. Без
   * значения (напр. в тестах) ссылки работают без state — «Назад» уйдёт на фолбэк `/staff`.
   */
  backFrom?: string
  /**
   * «Изменить»/«Удалить» (centrika-docs!78) — опциональны: кнопки в заголовке карточки рендерятся
   * ТОЛЬКО когда обработчики переданы (страница места, доступные роли). Без них карточка остаётся
   * read-only (напр. `sout/structure-modal.tsx`, где действий места нет). `deleting` — идёт
   * удаление: обе кнопки недоступны.
   */
  onEdit?: () => void
  onDelete?: () => void
  deleting?: boolean
}) {
  const view = OT_STATUS_VIEW[workplace.overallStatus]
  return (
    <div className="flex flex-col gap-4">
      <Card
        header={
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <span>{workplace.position}</span>
              {workplace.hasNoEmployee ? (
                <Badge variant="gray">рабочее место без сотрудника</Badge>
              ) : null}
            </div>
            {/* В правой части заголовка слева направо: общий статус, «Изменить», «Удалить»
                (centrika-docs!78, критерий приёмки 1). */}
            <div className="flex items-center gap-2">
              <Badge status={view.role}>{view.label}</Badge>
              {onEdit ? (
                <Button type="button" variant="secondary" size="sm" onClick={onEdit} disabled={deleting}>
                  Изменить
                </Button>
              ) : null}
              {onDelete ? (
                <Button type="button" variant="secondary" size="sm" onClick={onDelete} disabled={deleting}>
                  Удалить
                </Button>
              ) : null}
            </div>
          </div>
        }
      >
        <Row label="Подразделение" value={workplace.departmentName} />
        <Row label="Должность" value={workplace.position} />
        {!workplace.hasNoEmployee && workplace.employees.length > 0 ? (
          <Row
            label={workplace.employees.length > 1 ? 'Сотрудники' : 'Сотрудник'}
            value={employeeLinks(workplace.employees, backFrom)}
          />
        ) : null}
        {workplace.shiftPartners.length > 0 ? (
          <Row label="Сменщики" value={employeeLinks(workplace.shiftPartners, backFrom)} />
        ) : null}
        {/* Режим работы (centrika-docs!78, §основные сведения 5–8) — показываем строку только при
            заполненном значении: карточка используется и в read-only контекстах (sout-модалка),
            где режим может быть не задан. */}
        {workplace.actualAddress ? (
          <Row label="Фактический адрес рабочего места" value={workplace.actualAddress} />
        ) : null}
        {workplace.shiftHours != null ? (
          <Row label="Продолжительность смены, часов" value={formatWorkHours(workplace.shiftHours)} />
        ) : null}
        {workplace.weekHours != null ? (
          <Row label="Количество рабочих часов в неделю" value={formatWorkHours(workplace.weekHours)} />
        ) : null}
        {workplace.schedule ? <Row label="График работы" value={workplace.schedule} /> : null}
      </Card>

      <Grid cols={2}>
        <Card header="Сведения по СОУТ">
          {workplace.soutSummary ? (
            <>
              <Row label="Класс условий труда" value={workplace.soutSummary.hazardClass} />
              <Row label="Дата проведения" value={formatDate(workplace.soutSummary.conductedOn)} />
              <Row
                label="Срок действия"
                value={workplace.soutSummary.perpetual ? 'бессрочно' : formatDate(workplace.soutSummary.validUntil)}
              />
              {/* «N раб.места» (centrika-docs!90) — номер из ОТОБРАЖАЕМОГО результата СОУТ,
                  после срока действия. Видят клиент, администратор и сотрудник Центрики
                  (карточка одна на всех); номера нет — «—». */}
              <Row label="N раб.места" value={workplace.soutSummary.rmNumber?.trim() || '—'} />
            </>
          ) : (
            <EmptyState
              icon={ShieldCheck}
              title="Оценка СОУТ ещё не проводилась"
              description="Сведения появятся после первого результата СОУТ"
            />
          )}
          {/* Историческая СОУТ живёт ВНУТРИ этой карточки, под сведениями о последнем результате
              либо под пустым состоянием (centrika-docs!28) — по образцу карточки сотрудника.
              «Хронология СОУТ» остаётся отдельной карточкой ниже. */}
          <HistoricalSoutPanel
            workplaceId={workplace.id}
            openLabel="Добавить историческую СОУТ"
            inlineRecords
            onSaved={onSoutHistoricalSaved}
          />
        </Card>
        <Card header="Сведения по профрискам">
          {workplace.profriskSummary ? (
            <>
              <Row label="Уровень риска" value={RISK_LEVEL_LABELS[workplace.profriskSummary.riskLevel]} />
              <Row label="Дата оценки" value={formatDate(workplace.profriskSummary.assessedOn)} />
            </>
          ) : (
            <EmptyState
              icon={AlertTriangle}
              title="Оценка профрисков ещё не проводилась"
              description="Сведения появятся после первого результата профрисков"
            />
          )}
        </Card>
      </Grid>

      <Card header="Связанные документы">
        {workplace.documents.length === 0 ? (
          <EmptyState icon={FileText} title="Документов пока нет" />
        ) : (
          <div>
            {workplace.documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between gap-3 border-b border-line py-2.5 last:border-b-0">
                <span className="font-medium text-txt">{doc.title}</span>
                <div className="flex items-center gap-3 text-13 text-txt-secondary">
                  <span>{formatDate(doc.date)}</span>
                  <a className="text-accent underline" href={doc.downloadUrl} download>
                    Скачать
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Хронология (F2-13): ВСЕ видимые результаты (не только последний, как сводки выше) —
          раздельно СОУТ (по месту) и профриски (по подразделение+должность места), свежие
          сверху, каждая запись с источником (заказ/история). Общий каркас — HistoryCard. */}
      <Grid cols={2}>
        <HistoryCard
          header="Хронология СОУТ"
          items={workplace.soutHistory}
          primary={(item) => `Класс ${item.hazardClass || '—'}`}
          dateLabel={(item) => (item.conductedOn ? formatDate(item.conductedOn) : '—')}
        />
        <HistoryCard
          header="Хронология профрисков"
          items={workplace.profriskHistory}
          primary={(item) => RISK_LEVEL_LABELS[item.riskLevel]}
          dateLabel={(item) => (item.assessedOn ? formatDate(item.assessedOn) : '—')}
        />
      </Grid>
    </div>
  )
}
